// src/pages/cadastros/Prestadores/PrestadoresPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";

import { pode } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import { collection, getDocs, query, where, deleteDoc, doc } from "firebase/firestore";

function renderCodigo(p) {
  if (p.codigo) return p.codigo;
  if (p.codigoSequencial != null) {
    return `PS${String(p.codigoSequencial).padStart(4, "0")}`;
  }
  if (p.id) return p.id.slice(0, 8);
  return "-";
}

function renderEspecialidades(p) {
  if (Array.isArray(p.especialidades) && p.especialidades.length) {
    return p.especialidades.join(", ");
  }
  if (typeof p.especialidades === "string") return p.especialidades;
  return "-";
}

function PrestadoresPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const isGlobalAccess = !!usuarioLogado?.globalAccess || usuarioLogado?.tipoAcesso === "GLOBAL";

  const canVer = pode(usuarioLogado, "cadastros", "prestadores", "tabela");
  const canCriar = pode(usuarioLogado, "cadastros", "prestadores", "form");
  const canEditar = pode(usuarioLogado, "cadastros", "prestadores", "altera");

  const [prestadores, setPrestadores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nomeFantasia");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nomeFantasia", label: "Nome" },
    { value: "especialidades", label: "Especialidades" },
    { value: "contatoNome", label: "Contato" },
    { value: "telefone", label: "Telefone" },
    { value: "email", label: "Email" },
  ];

  useEffect(() => {
    async function carregarPrestadores() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "prestadores");
        const filtrosBase = [];

        if (usuarioLogado.grupoId) {
          filtrosBase.push(where("grupoId", "==", usuarioLogado.grupoId));
        } else {
          filtrosBase.push(where("nomeFantasia", "==", "__nulo__"));
        }

        if (!modoGlobal && empresaAtual) {
          const filtros = [...filtrosBase];
          if (empresaAtual.id) filtros.push(where("empresaId", "==", empresaAtual.id));
          else if (empresaAtual.nome) filtros.push(where("empresaNome", "==", empresaAtual.nome));

          const snap = await getDocs(query(ref, ...filtros));
          const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          lista.sort((a, b) => {
            const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
            const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
            return tb - ta;
          });
          setPrestadores(lista);
          return;
        }

        if (modoGlobal && !isGlobalAccess) {
          const empresas = Array.isArray(usuarioLogado.empresasPermitidas) ? usuarioLogado.empresasPermitidas : [];
          if (empresas.length === 0) {
            setPrestadores([]);
            setErro("Seu usuário não possui acesso global e não possui empresas permitidas para consulta.");
            return;
          }

          const snaps = await Promise.all(
            empresas.map((emp) => {
              const filtros = [...filtrosBase];
              if (emp?.id) filtros.push(where("empresaId", "==", emp.id));
              else if (emp?.nome) filtros.push(where("empresaNome", "==", emp.nome));
              else filtros.push(where("nomeFantasia", "==", "__nulo__"));
              return getDocs(query(ref, ...filtros));
            })
          );

          const map = new Map();
          for (const s of snaps) {
            for (const d of s.docs) {
              map.set(d.id, { id: d.id, ...d.data() });
            }
          }
          const lista = Array.from(map.values());
          lista.sort((a, b) => {
            const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
            const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
            return tb - ta;
          });
          setPrestadores(lista);
          return;
        }

        const snap = await getDocs(query(ref, ...filtrosBase));
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        lista.sort((a, b) => {
          const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return tb - ta;
        });

        setPrestadores(lista);

      } catch (e) {
        console.error("Erro ao carregar prestadores:", e);
        const code = e?.code ? String(e.code) : "";
        const msg = e?.message ? String(e.message) : "";
        setErro(
          code || msg
            ? `Erro ao carregar prestadores: ${code || msg}`
            : "Erro ao carregar prestadores. Tente novamente."
        );
      } finally {
        setCarregando(false);
      }
    }

    if (canVer) carregarPrestadores();
    else {
      setPrestadores([]);
      setCarregando(false);
      setErro("Sem permissão para visualizar Prestadores.");
    }
  }, [usuarioLogado, modoGlobal, empresaAtual, canVer, isGlobalAccess]);

  const prestadoresFiltrados = prestadores.filter((p) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigo(p);
    } else if (campoFiltro === "especialidades") {
      valorBase = renderEspecialidades(p);
    } else {
      valorBase = p[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  const { sortedData, requestSort, getSortDirection } = useSortableTable(prestadoresFiltrados, "nomeFantasia");

  async function handleExcluir(id) {
    if (!id) return;
    const ok = window.confirm("Tem certeza que deseja excluir este prestador? Essa ação não pode ser desfeita.");
    if (!ok) return;

    setCarregando(true);
    setErro("");

    try {
      await deleteDoc(doc(db, "prestadores", id));
      setPrestadores((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Erro ao excluir prestador:", e);
      setErro("Erro ao excluir prestador. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = prestadoresFiltrados.map((p) => ({
    id: p.id,
    nome: p.nomeFantasia || "Sem nome",
    subtitulo: renderEspecialidades(p),
    detalhes: [
      { icone: "🔢", label: "Código", valor: renderCodigo(p) },
      { icone: "👤", label: "Contato", valor: p.contatoNome || "-" },
      { icone: "📞", label: "Telefone", valor: p.telefone || "-" },
      { icone: "✉️", label: "Email", valor: p.email || "-" },
      { icone: "📝", label: "Obs.", valor: p.observacoes || "-" },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Prestadores"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando prestadores..."
          titulo="Prestadores"
          searchPlaceholder="Buscar prestador..."
          novoLabel="Novo Prestador"
          emptyMessage="Nenhum prestador encontrado"
          onHome={() => navigate("/")}
          onNovo={canCriar ? () => navigate("/cadastros/prestadores/novo") : undefined}
          onEditar={canEditar ? (id) => navigate(`/cadastros/prestadores/editar/${id}`) : undefined}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Prestadores de Serviço"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Prestadores cadastrados</h3>
            <p>Cadastro de empresas/contatos que realizam serviços externos (terceirizados).</p>
          </div>

          <div className="pc-table-header-actions">
            {canCriar && (
              <Can modulo="cadastros" recurso="prestadores" acao="form">
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={() => navigate("/cadastros/prestadores/novo")}
                >
                  + Novo Prestador
                </button>
              </Can>
            )}
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={canCriar ? () => navigate("/cadastros/prestadores/novo") : undefined}
          placeholder="Filtrar prestadores..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando prestadores...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum prestador encontrado.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader label="Código" sortKey="codigoSequencial" onSort={requestSort} sortDirection={getSortDirection("codigoSequencial")} />
                  <SortableHeader label="Nome" sortKey="nomeFantasia" onSort={requestSort} sortDirection={getSortDirection("nomeFantasia")} />
                  <SortableHeader label="Especialidades" sortKey="especialidades" onSort={requestSort} sortDirection={getSortDirection("especialidades")} />
                  <SortableHeader label="Contato" sortKey="contatoNome" onSort={requestSort} sortDirection={getSortDirection("contatoNome")} />
                  <SortableHeader label="Telefone" sortKey="telefone" onSort={requestSort} sortDirection={getSortDirection("telefone")} />
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((p) => {
                  const aberto = expandedId === p.id;

                  return (
                    <React.Fragment key={p.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === p.id ? null : p.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>

                        <td>{renderCodigo(p)}</td>
                        <td style={{ fontWeight: 600 }}>{p.nomeFantasia || "-"}</td>
                        <td>{renderEspecialidades(p)}</td>
                        <td>{p.contatoNome || "-"}</td>
                        <td>{p.telefone || "-"}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {canEditar && (
                            <Can modulo="cadastros" recurso="prestadores" acao="altera">
<button
                              type="button"
                              className="pc-icon-btn"
                              title="Editar prestador"
                              onClick={() => navigate(`/cadastros/prestadores/editar/${p.id}`)}
                            >
                              ✏️
                            </button>
</Can>
                          )}
                          {canEditar && (
                            <Can modulo="cadastros" recurso="prestadores" acao="altera">
<button
                              type="button"
                              className="pc-icon-btn"
                              title="Apagar prestador"
                              onClick={() => handleExcluir(p.id)}
                            >
                              🗑️
                            </button>
</Can>
                          )}
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Email</div>
                                  <div className="pc-expand-value">{p.email || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">CNPJ</div>
                                  <div className="pc-expand-value">{p.cnpj || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Endereço</div>
                                  <div className="pc-expand-value">{p.endereco || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Observações</div>
                                  <div className="pc-expand-value">{p.observacoes || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">ID</div>
                                  <div className="pc-expand-value">{p.id}</div>
                                </div>
                              </div>

                              <div className="pc-expand-actions">
                                {canEditar && (
                                  <button
                                    type="button"
                                    className="pc-btn-secundario"
                                    onClick={() => navigate(`/cadastros/prestadores/editar/${p.id}`)}
                                  >
                                    Editar
                                  </button>
                                )}

                                {canEditar && (
                                  <button
                                    type="button"
                                    className="pc-btn-perigo"
                                    onClick={() => handleExcluir(p.id)}
                                  >
                                    Excluir
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default PrestadoresPage;
